<template>
    <div class="outer">
        <div class="meta-box">
            <h1>User Management</h1>
            <div class="controls">
                <Button
                    v-if="canCreateNewUsers"
                    icon="pi pi-user-plus"
                    label="Add User"
                    severity="primary"
                    @click="openAddUserDialog"
                />
                <IconField>
                    <InputIcon>
                        <i class="pi pi-search" />
                    </InputIcon>
                    <InputText
                        v-model="filters['global'].value"
                        placeholder="Search"
                    />
                </IconField>
            </div>
        </div>

        <div v-if="isLoading" class="loading-container">
            <Spinner />
        </div>

        <div v-else-if="currentError" class="error-message">
            <Message severity="error">{{ currentError }}</Message>
        </div>

        <div v-else-if="users.length === 0" class="empty-state">
            <Message severity="info"
                >No users found in this organization.</Message
            >
        </div>

        <DataTable
            v-model:filters="filters"
            :value="users"
            :rowHover="true"
            dataKey="identity"
            selectionMode="single"
            v-model:selection="selectedUser"
            class="user-table"
            :rowClass="rowClass"
            :paginator="users.length > 10"
            :rows="10"
            :rowsPerPageOptions="[5, 10, 20]"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :globalFilterFields="['email', 'name']"
        >
            <Column field="email" header="Email" sortable />
            <Column field="name" header="Name" sortable />
            <Column field="role" header="Role" sortable>
                <template #body="{ data }">
                    <span class="role-badge" :class="roleClass(data.role)">
                        {{ formatRole(data.role) }}
                    </span>
                </template>
            </Column>
            <Column
                v-if="canCreateNewUsers"
                header="Actions"
                :exportable="false"
            >
                <template #body="{ data }">
                    <div
                        class="action-buttons"
                        v-if="data.email !== user?.email"
                    >
                        <Button
                            icon="pi pi-user-edit"
                            severity="secondary"
                            text
                            rounded
                            @click="openEditUserDialog(data)"
                            v-tooltip.top="'Edit user'"
                        />
                        <Button
                            icon="pi pi-trash"
                            severity="danger"
                            text
                            rounded
                            @click="confirmRemoveUser(data)"
                            v-tooltip.top="'Remove user'"
                        />
                    </div>
                    <div v-else>
                        <span class="current-user-label">Current user</span>
                    </div>
                </template>
            </Column>
        </DataTable>

        <!-- Dialogs will be rendered by the DialogService automatically -->

        <!-- Confirmation Dialog -->
        <ConfirmDialog></ConfirmDialog>
    </div>
</template>

<script setup lang="ts">
import { useOrganizationManager } from "@/composables/organization-manager";
import { useUserManagementDialog } from "@/composables/user-management-dialog";
import { ClusterRole, useUserManager } from "@/stores/user-store";
import {
    type OrganizationUser,
    useUsersManager,
    useUsersStore,
} from "@/stores/users-store";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, onMounted, ref, watch } from "vue";

// PrimeVue Components
import Button from "primevue/button";
import Column from "primevue/column";
import ConfirmDialog from "primevue/confirmdialog";
import DataTable from "primevue/datatable";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Message from "primevue/message";

// Shared Components
import Spinner from "@/shared/Spinner.vue";

// User role management
const usersStore = useUsersStore();
const { users, isLoading, currentError } = useUsersManager();
const { user, currentOrganizationRole } = useUserManager();
const { organization } = useOrganizationManager();
const confirm = useConfirm();
const toast = useToast();

// Table state
const selectedUser = ref<OrganizationUser>();

// Setup filters for DataTable
const filters = ref({
    global: { value: null, matchMode: "contains" },
});

// Init user management dialog
const userDialog = useUserManagementDialog();

const canCreateNewUsers = computed(() => {
    return currentOrganizationRole.value === ClusterRole.USER_MANAGER;
});

// Format role for display
const formatRole = (role: string) => {
    switch (role) {
        case "user-manager":
            return "User Manager";
        case "user":
            return "User";
        default:
            return role;
    }
};

// Role badge styling
const roleClass = (role: string) => {
    return {
        "role-admin": role === "user-manager",
        "role-user": role === "user",
    };
};

// Row styling
const rowClass = (data: OrganizationUser) => {
    return {
        "current-user-row": data.email === user.value?.email,
        "manager-user-row": data.role === "user-manager",
    };
};

// Load users when component mounts or organization changes
onMounted(async () => {
    await usersStore.getAllUsers();
});

watch(
    () => organization.value,
    async () => {
        if (organization.value) {
            await usersStore.getAllUsers();
        }
    },
    { immediate: true }
);

// Dialog management
const openAddUserDialog = () => {
    userDialog.openAddUser();
};

const openEditUserDialog = (user: OrganizationUser) => {
    userDialog.openEditUser(user);
};

// Remove user confirmation
const confirmRemoveUser = (userData: OrganizationUser) => {
    // Don't allow removing yourself
    if (userData.email === user.value?.email) {
        toast.add({
            severity: "warn",
            summary: "Cannot remove yourself",
            detail: "You cannot remove your own user account from the organization",
            life: 3000,
        });
        return;
    }

    confirm.require({
        message: `Are you sure you want to remove ${userData.name || userData.email} from this organization?`,
        header: "Remove User",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        accept: () => removeUser(userData.identity),
    });
};

const removeUser = async (userId: string) => {
    await usersStore.removeUserFromOrganization(userId);
};
</script>

<style scoped lang="scss">
.outer {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    padding: 20px;
}

.meta-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    h1 {
        margin: 0;
    }
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
}

.error-message {
    margin: 20px 0;
}

.user-table {
    margin-bottom: 20px;
}

.role-badge {
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: 500;
    display: inline-block;

    &.role-admin {
        background-color: var(--blue-100);
        color: var(--blue-700);
        border: 1px solid var(--blue-200);
    }

    &.role-user {
        background-color: var(--surface-100);
        color: var(--text-color-secondary);
        border: 1px solid var(--surface-200);
    }
}

.current-user-row {
    background-color: var(--surface-100) !important;
    opacity: 0.7;
    color: var(--text-color-secondary);
}

.manager-user-row {
    font-weight: 500;
}

.current-user-label {
    font-size: 0.875rem;
    color: var(--text-color-secondary);
    font-style: italic;
}

.action-buttons {
    display: flex;
    gap: 8px;
}

.user-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
        font-weight: 500;
    }
}

.w-full {
    width: 100%;
}

.controls {
    display: flex;
    justify-content: space-between;
    gap: 16px;
}
</style>
